import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { StaffService } from '../api/StaffAPI';
import s from '../styles/staff.module.css';
import logoLight from '../assets/images/icon-only-black.png';
import logoDark from '../assets/images/icon-only-bronze.png';

function locationLabel(localtionIds, locationNames) {
  if (!localtionIds) return '—';
  if (localtionIds === 'all') return 'All';
  const names = localtionIds.split(',').map((id) => locationNames[id] || 'Others');
  return [...new Set(names)].join(', ');
}

const STATUS_FILTERS = ['all', 'active', 'inactive'];

export function StaffTablePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lightMode, toggleLightMode] = useLightMode();
  const isAdmin = user?.role === 'admin';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['staffs'],
    queryFn: () => StaffService.list(),
  });

  const staffs = data?.staffs || [];
  const locationNames = data?.locations || {};

  const [statusFilter, setStatusFilter] = useState('active');
  const visibleStaffs = statusFilter === 'all'
    ? staffs
    : staffs.filter((m) => String(m.status || '').toLowerCase() === statusFilter);

  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError('');
    try {
      await StaffService.sync();
      await refetch();
    } catch (err) {
      setError(err?.error || err?.message || 'Failed to sync from Square');
    } finally {
      setSyncing(false);
    }
  }, [refetch]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <header className={s.header}>
        <span className={s.logo}>
          <img className={s.logoImg} src={lightMode ? logoLight : logoDark} alt="ACE CAR SPA" />
        </span>
        <div className={s.headerActions}>
          {isAdmin && (
            <button onClick={handleSync} className={s.primaryBtn} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync from Square'}
            </button>
          )}
          <Link to="/dashboard" className={s.linkBtn}>← Dashboard</Link>
          <button onClick={() => toggleLightMode(l => !l)} className={s.linkBtn}>
            {lightMode ? 'Dark Mode' : 'Light Mode'}
          </button>
          <button onClick={handleLogout} className={s.linkBtn}>Logout</button>
        </div>
      </header>

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tableHeaderRow}>
            <div className={s.sectionLabel} style={{ marginBottom: 0 }}>Staff ({visibleStaffs.length})</div>
            <div className={s.filterBtns} role="group" aria-label="Filter by status">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f}
                  className={`${s.filterBtn} ${statusFilter === f ? s.filterBtnActive : ''}`}
                  onClick={() => setStatusFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {error && <div className={s.empty} style={{ color: '#e0685f' }}>{error}</div>}
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Locations</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={4} className={s.loading}>Loading…</td></tr>
                )}
                {!isLoading && visibleStaffs.length === 0 && (
                  <tr><td colSpan={4} className={s.empty}>
                    {staffs.length === 0
                      ? <>No staff yet. {isAdmin ? 'Click “Sync from Square”.' : ''}</>
                      : `No ${statusFilter === 'all' ? '' : statusFilter + ' '}staff.`}
                  </td></tr>
                )}
                {!isLoading && visibleStaffs.map((m) => (
                  <tr key={m.idStaff}>
                    <td>{`${m.given_name || ''} ${m.family_name || ''}`.trim() || '—'}</td>
                    <td>{m.email || '—'}</td>
                    <td>{locationLabel(m.localtionIds, locationNames)}</td>
                    <td>{m.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
